<template>
  <b-modal ref="statement-task-modal"
           modal-class="task-modal v2-modal"
           size="xl"
           @hidden="onHidden">
    <template #modal-header>
      <div class="v2-modal__head">
        <div class="v2-modal__titles">
          <div class="v2-modal__eyebrow">{{ task ? task.project.title : '' }}</div>
          <h3 data-qid="statement-task-title">{{ task ? $t(fieldConfig.title) : '' }}</h3>
        </div>
        <span class="task-panel__nav" @click="hide" data-qid="statement-task-close"><font-awesome-icon icon="times"/></span>
      </div>
    </template>

    <div v-if="!task" class="text-center text-muted">
      <font-awesome-icon icon="spinner" class="fa-spin"/>
    </div>
    <template v-else>
      <p class="text-muted">{{ $t(delivered ? 'projects.measures.task_delivered_text' : 'projects.measures.task_intro') }}</p>
      <StatementTextPanel :project="text"
                          :field="task.field"
                          :title="fieldConfig.title"
                          :placeholder="fieldConfig.placeholder"
                          :missing-label="fieldConfig.missing"
                          :tag="fieldConfig.tag"
                          :icon="fieldConfig.icon"
                          :can-edit="task.can_edit"
                          :statement-task="task"
                          qid="statement-task"
                          @updated="onUpdated"
      />
    </template>

    <template #modal-footer>
      <span v-if="delivered" class="home-tag is-teal mr-auto" data-qid="statement-task-delivered">
        {{ $t('projects.measures.task_status_delivered', { name: task.delivered_by ? task.delivered_by.name : '', date: $options.filters.formatDate(task.delivered_at) }) }}
      </span>
      <button type="button" class="btn btn-secondary" @click="hide">{{ $t('system.close') }}</button>
      <button v-if="task && isMine && !delivered"
              type="button"
              class="btn btn-success"
              :disabled="delivering || !hasText"
              data-qid="statement-task-deliver"
              @click="deliver"
      >
        <font-awesome-icon icon="paper-plane" class="mr-1"/>{{ $t('projects.measures.task_deliver') }}
      </button>
    </template>
  </b-modal>
</template>

<script>
import piincHttp from '@/util/configureAxios.js';
import StatementTextPanel from '@/views/Projects/StatementTextPanel.vue';
import { richTextToPlainText } from '@/components/richText/richText';

const FIELDS = {
  management_statement: {
    title: 'projects.measures.management_statement_title',
    placeholder: 'projects.measures.management_statement_placeholder',
    missing: 'projects.measures.missing_management_statement',
    tag: 'management.statement',
    icon: 'file-signature'
  },
  system_description: {
    title: 'projects.measures.system_description_title',
    placeholder: 'projects.measures.system_description_placeholder',
    missing: 'projects.measures.missing_system_description',
    tag: 'system.description',
    icon: 'desktop'
  }
};

// A management statement or system description sent to a documentation collection user (US 9394), opened
// from Home: the same text, editor and Word import as on the project's System description tab, and Deliver
export default {
  name: 'StatementTaskModal',
  components: {
    StatementTextPanel
  },
  data() {
    return {
      task: null,
      text: {},
      delivering: false,
      changed: false
    };
  },
  computed: {
    fieldConfig() {
      return this.task ? FIELDS[this.task.field] : FIELDS.management_statement;
    },
    user() {
      return this.$store.state.user || {};
    },
    isMine() {
      return this.task.users.some((user) => user.id === this.user.id);
    },
    delivered() {
      return this.task && this.task.status === 'delivered';
    },
    hasText() {
      return richTextToPlainText(this.text[this.task.field]).trim() !== '';
    }
  },
  methods: {
    open(id) {
      this.task = null;
      this.changed = false;
      this.$refs['statement-task-modal'].show();
      piincHttp.get('statement-tasks/' + id).then((response) => {
        this.task = response.data;
        // The panel shows and saves the text as the project's own field
        this.text = { slug: this.task.project.slug, [this.task.field]: this.task.text };
      }, () => {
        this.hide();
      });
    },
    hide() {
      this.$refs['statement-task-modal'].hide();
    },
    onUpdated(text) {
      this.text = text;
      this.changed = true;
    },
    deliver() {
      this.delivering = true;
      piincHttp.post('statement-tasks/' + this.task.id + '/deliver').then((response) => {
        this.task = Object.assign({}, this.task, response.data, { can_edit: false });
        this.changed = true;
        this.$toastr('success', this.$t('projects.measures.task_deliver_success'));
        this.hide();
      }, (error) => {
        this.$toastr('error', (error && error.data && error.data.message) || this.$t('projects.measures.save_failed'));
      }).finally(() => {
        this.delivering = false;
      });
    },
    onHidden() {
      if (this.changed) {
        this.$emit('changed');
      }
    }
  }
};
</script>
